/**
 * Provides a context for managing state and selectors related to Notification components
 * @module NotificationContext
 */
import { REST_API } from '@awp/constants';
import { GetJson } from '@awp/helpers/fetchReact';
import { BOOTSTRAP_BREAKPOINTS, checkBreakpoint } from '@awp/helpers/UIHelper';
import { debounce } from '@awp/helpers/Utils';
import { selectContractAreas, selectEetAreas, selectWeatherStationObservationTypes } from '@awp/stores/dropdownsSlice';
import { selectCurrentUser } from '@awp/stores/usersSlice';
import React, { createContext, useContext, useRef, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { defaultDto, getDefaultNotificationMethods } from './NotificationCommon';

/**
 * Context for managing state and selectors related to Notification components
 * @type {React.Context}
 */
const NotificationContext = createContext();

/**
 * Provider component for NotificationContext.
 * @param {object} props - Component props.
 * @param {React.ReactNode} props.children - Child components wrapped by the provider.
 * @returns {React.ReactNode} Provider component.
 */
export const NotificationContextProvider = ({ children }) => {
  const dispatch = useDispatch();

  // Selectors for current user and weather station observation types from Redux store
  const currentUser = useSelector(selectCurrentUser);
  const weatherStationsObsTypes = useSelector(selectWeatherStationObservationTypes);

  const contractAreas = useSelector(selectContractAreas);
  const eetAreas = useSelector(selectEetAreas);

  // State variables for managing the context
  const [mode, setMode] = useState('list');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [notificationId, setNotificationId] = useState(null);
  const [showYesNoModal, setShowYesNoModal] = useState(false);
  const [search, setSearch] = useState('');
  const [stationFilter, setStationFilter] = useState(true);
  const [errors, setErrors] = useState(null);
  const [reload, setReload] = useState(false);

  const [notificationMethods, setNotificationMethods] = useState({ email: true, sms: false });

  useMemo(() => {
    setNotificationMethods(getDefaultNotificationMethods(data.find((v) => v.NotificationTriggerId === notificationId))); // Get default notification methods
  }, [data, notificationId]);

  // State for handling responsive breakpoints
  const [breakpoints, setBreakpoints] = useState({
    isMedium: checkBreakpoint(BOOTSTRAP_BREAKPOINTS.MEDIUM),
    isLarge: checkBreakpoint(BOOTSTRAP_BREAKPOINTS.LARGE),
  });

  // Memoized fetch data on mount
  useMemo(() => {
    setLoading(true);
    const url = REST_API.NOTIFICATIONS;

    GetJson(url)
      .then((response) => response.json())
      .then((data) => {
        setData(data); // Set full data
        setFilteredData(data); // Set filtered data
        setLoading(false); // Stop loading indicator
        //setReload(false);
      });
  }, []);

  // Effect for handling window resize and debounce
  useEffect(() => {
    const handleResize = debounce(() => {
      setBreakpoints({
        isMedium: checkBreakpoint(BOOTSTRAP_BREAKPOINTS.MEDIUM),
        isLarge: checkBreakpoint(BOOTSTRAP_BREAKPOINTS.LARGE),
      });
    }, 100);

    window.addEventListener('resize', handleResize);

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Structure the context values for the provider
  const stateValues = {
    mode,
    data,
    loading,
    notificationId,
    search,
    breakpoints,
    filteredData,
    showYesNoModal,
    stationFilter,
    errors,
    notificationMethods,
    reload,
  };

  // State setters to be passed to the context
  const stateSetters = {
    setMode,
    setData,
    setLoading,
    setNotificationId,
    setSearch,
    setBreakpoints,
    setFilteredData,
    setShowYesNoModal,
    setStationFilter,
    setErrors,
    setNotificationMethods,
    setReload,
  };

  // Selector values to be passed to the context
  const selectorValues = { currentUser, weatherStationsObsTypes, contractAreas, eetAreas };

  const memoizedValues = {};

  // Combine values into a single context value
  const contextValue = {
    ...stateValues,
    ...stateSetters,
    ...selectorValues,
    ...memoizedValues,
  };

  return <NotificationContext.Provider value={contextValue}>{children}</NotificationContext.Provider>;
};

/**
 * Hook for consuming NotificationContext.
 * @returns {object} Context value.
 */
export const useNotificationContext = () => useContext(NotificationContext);
